import get from 'lodash/get'
import { Address } from 'app-types'

import { ReduxState } from '../../types'
import { CodeText } from '../../redux/reducers/codetable/model'
import { EMPLOYMENT_TYPES, PepOptions } from '../../constants'
import {
  getClientInfoForm,
  getClientAddressForm,
  getClientDetailsForm,
  getFinanceProfessionForm,
  getFinanceEmployerForm,
  getFinanceIncomeForm,
  getFinanceExpensesForm,
  getClientAdditionalInfoForm,
  getCacheAuthForm,
} from '../formSelectors'
import { czDateToUnixTime } from '../../utils/dates'

export const getClientInfo = (state: ReduxState) => {
  const clientInfoForm = getClientInfoForm(state)

  return {
    consentCommUnsecured: clientInfoForm?.consentCommUnsecured,
    consentInstantMessaging: clientInfoForm?.consentInstantMessaging,
    consentToOfferMarketing: clientInfoForm?.consentToOfferMarketing,
    email: clientInfoForm?.email,
    firstName: clientInfoForm?.firstName,
    lastName: clientInfoForm?.lastName,
    phoneNumber: clientInfoForm?.phoneNumber,
    warningsConfirmed: clientInfoForm?.warningsConfirmed,
  }
}

const trimZip = (zip: string) => zip.replace(/ /g, '')

const mapAddress = (address: Address) => ({ ...address, zip: trimZip(address.zip) })

export const getClientAddresses = (state: ReduxState) => {
  const clientAddressForm = getClientAddressForm(state)

  return {
    permanentAddress: mapAddress(get(clientAddressForm, 'permanentAddress')),
    contactAddress: mapAddress(get(clientAddressForm, 'contactAddress')),
    differentContactAddress: get(clientAddressForm, 'differentContactAddress'),
  }
}

export const getClientAddressesForApi = (state: ReduxState) => {
  const { permanentAddress, contactAddress, differentContactAddress } = getClientAddresses(state)

  return {
    permanentAddress,
    contactAddress: differentContactAddress ? contactAddress : permanentAddress,
  }
}

const trimSlash = (pin: string) => pin.replace(/\//, '')

export const getClientDetails = (state: ReduxState) => {
  const clientDetailsForm = getClientDetailsForm(state)

  return {
    identityCardNumber: get(clientDetailsForm, 'identityCardNumber'),
    identityCardExpiration: czDateToUnixTime(get(clientDetailsForm, 'identityCardExpiration')),
    personalIdentificationNumber: trimSlash(get(clientDetailsForm, 'personalIdentificationNumber')),
    maritalStatus: get(clientDetailsForm, 'maritalStatus'),
    citizenship: get(clientDetailsForm, 'citizenship'),
    placeOfBirth: get(clientDetailsForm, 'placeOfBirth'),
    countryOfBirth: get(clientDetailsForm, 'countryOfBirth'),
  }
}

export const getFinanceIncome = (state: ReduxState) => {
  const financeIncomeForm = getFinanceIncomeForm(state)

  return {
    net3MonthIncome: get(financeIncomeForm, 'net3MonthIncome'),
    net3MonthHouseholdIncome: get(financeIncomeForm, 'net3MonthHouseholdIncome'),
    taxCrOnlyFlag: get(financeIncomeForm, 'taxCrOnlyFlag'),
  }
}

export const getFinanceExpenses = (state: ReduxState) => {
  const financeExpensesForm = getFinanceExpensesForm(state)

  return {
    housingExpenditure: get(financeExpensesForm, 'housingExpenditure'),
    otherMonthlyInstallments: get(financeExpensesForm, 'otherMonthlyInstallments'),
    otherCostOfLiving: get(financeExpensesForm, 'otherCostOfLiving'),
    numberOfDependents: get(financeExpensesForm, 'numberOfDependents'),
  }
}

export const getFinanceEmployer = (state: ReduxState) => {
  const financeEmployerForm = getFinanceEmployerForm(state)

  const shortTermJob = state.client.employmentTypeCode === EMPLOYMENT_TYPES.na_dobu_urcitou

  const employedFrom = czDateToUnixTime(get(financeEmployerForm, 'employedSince'))
  const employedTo = czDateToUnixTime(get(financeEmployerForm, 'employedTo'))

  return {
    employedSince: employedFrom,
    employedTo: shortTermJob ? employedTo : undefined,
    employerAddress: mapAddress(get(financeEmployerForm, 'employerAddress')),
    employerId: get(financeEmployerForm, 'employerId'),
    employerName: get(financeEmployerForm, 'employerName'),
    noticePeriod: !!get(financeEmployerForm, 'noticePeriod', false),
    trialPeriod: !!get(financeEmployerForm, 'trialPeriod', false),
  }
}

export const getFinanceProfession = (state: ReduxState) => {
  const financeProfessionForm = getFinanceProfessionForm(state)
  const employmentTypeCode = get(financeProfessionForm, 'employmentTypeCode')
  const occupationTypeCode = get(financeProfessionForm, 'professionCode')

  const employmentTypes = state.codetable.employmentTypes
  const occupationTypes = state.codetable.occupationsByEmploymentType[employmentTypeCode]

  const employmentType: CodeText = employmentTypes.find(
    (item: CodeText) => item.code === employmentTypeCode
  )
  const occupationType: CodeText = occupationTypes.find(
    (item: CodeText) => item.code === occupationTypeCode
  )

  return {
    employmentTypeCode,
    employmentTypeName: employmentType.text,
    professionCode: occupationTypeCode,
    professionName: occupationType.text,
  }
}

export const getClientAdditionalInfo = (state: ReduxState) => {
  const clientAdditionalInfoForm = getClientAdditionalInfoForm(state)

  return {
    education: get(clientAdditionalInfoForm, 'education'),
    stayOnAddressDuration: get(clientAdditionalInfoForm, 'stayOnAddressDuration'),
    livingType: get(clientAdditionalInfoForm, 'livingType'),
    otherDocumentType: get(clientAdditionalInfoForm, 'otherDocumentType'),
    pepReason:
      get(clientAdditionalInfoForm, 'pep') === PepOptions.Yes
        ? get(clientAdditionalInfoForm, 'pepReason')
        : undefined,
  }
}

export const getCacheAuthSmsCode = (state: ReduxState) => {
  const cacheAuthForm = getCacheAuthForm(state)
  const verificationCode = get(cacheAuthForm, 'smsCode')

  return { verificationCode }
}
